import type { CSSProperties } from "react";
import {
  MAIN_SPARKLINE_LINE_THEME,
  MAIN_SPARKLINE_BARS_PURPLE
} from "Constants/mainDashboardSparklines";

// --- Sparkline outer containers (storage / blogging / adverts) ---
export const SPARK_CONTAINER_USAGE_LARGE: CSSProperties = {
  height: "calc(40% - 38px)",
  width: "100%"
};

export const SPARK_CONTAINER_TABLE_30: CSSProperties = {
  height: "calc(30% - 38px)",
  width: "40px"
};

export const SPARK_CONTAINER_TABLE_35: CSSProperties = {
  height: "calc(35% - 38px)",
  width: "40px"
};

// --- Storage sparkline series styles ---
export const STORAGE_SPARK_LINE_WHITE_W3: CSSProperties = {
  strokeWidth: 3,
  stroke: "#fff",
  fill: "none"
};

export const STORAGE_SPARK_LINE_WHITE_W2: CSSProperties = {
  stroke: "white",
  strokeWidth: "2",
  fill: "none"
};

export const STORAGE_SPARK_BARS_WHITE: CSSProperties = {
  stroke: "white",
  strokeWidth: "1",
  fill: "#fff"
};

/** Re-export: blogging/adverts in-table bars (white stroke, theme purple fill). */
export const SPARK_BARS_TABLE_PURPLE = MAIN_SPARKLINE_BARS_PURPLE;

/** Re-export: 3px theme line, no fill (blogging cards + tables). */
export const SPARK_LINE_THEME_OUTLINE_3 = MAIN_SPARKLINE_LINE_THEME;

export const SPARK_LINE_WHITE_FILL_PURPLE: CSSProperties = {
  stroke: "white",
  strokeWidth: "1",
  fill: "#922c88"
};

// --- Layout / form widths ---
export const LAYOUT_MAX_WIDTH_220: CSSProperties = { maxWidth: 220 };
export const LAYOUT_MAX_WIDTH_280: CSSProperties = { maxWidth: 280 };
export const LAYOUT_MAX_WIDTH_360: CSSProperties = { maxWidth: 360 };
export const LAYOUT_MAX_WIDTH_400: CSSProperties = { maxWidth: 400 };

export const LAYOUT_MIN_WIDTH_260: CSSProperties = { minWidth: 260 };

export const LAYOUT_SELECT_SM_WIDTH: CSSProperties = { width: 72 };

export const CODE_TRUNCATE_FULL_ROW: CSSProperties = { maxWidth: "100%" };

/** Sparklines outer wrapper (charts demo page). */
export const SPARKLINES_CONTAINER_FULL_WIDTH: CSSProperties = { width: "100%" };

/** Logistics suite warehouse utilization strip (pair with dynamic `background`). */
export const LOGISTICS_WAREHOUSE_UTIL_HEIGHT: CSSProperties = { height: 80 };

/** DevOps error-budget mini histogram column (pair with dynamic height/opacity). */
export const DEVOPS_ERROR_BUDGET_BAR_COLUMN: CSSProperties = { width: 8 };

/** Industrial asset OEE bar (pair with dynamic `height: \`${v}%\``). */
export const INDUSTRIAL_OEE_BAR_MIN: CSSProperties = { minHeight: 4 };

/** Adverts placement chart column (pair with dynamic `height: \`${h}%\``). */
export const ADVERTS_PLACEMENT_BAR_COLUMN: CSSProperties = { width: "12%" };

/** Adverts campaign detail — static demo bar heights (%). */
export const ADVERTS_PLACEMENT_DEMO_BAR_STYLES: readonly CSSProperties[] = (
  [40, 65, 50, 80, 55] as const
).map(h => ({
  ...ADVERTS_PLACEMENT_BAR_COLUMN,
  height: `${h}%`
}));

/** AI Studio usage-by-model bar (pair with dynamic pixel height). */
export const AI_STUDIO_USAGE_BAR_WIDTH: CSSProperties = { width: "80%" };

/** Subscription PLG funnel outer segment (pair with dynamic width % / opacity). */
export const PLG_FUNNEL_STEP_SEGMENT_HEIGHT: CSSProperties = { height: 36 };

/** PLG trial funnel — three outer bands (fixed width % / opacity per step). */
export const PLG_TRIAL_FUNNEL_OUTER_STYLES: readonly CSSProperties[] = (
  [0, 1, 2] as const
).map(i => ({
  width: `${100 - i * 22}%`,
  ...PLG_FUNNEL_STEP_SEGMENT_HEIGHT,
  opacity: 1 - i * 0.15
}));

/** SaaS CRM accounts table health progress sizing. */
export const SAAS_CRM_TABLE_PROGRESS_CELL: CSSProperties = {
  width: 64,
  height: 6
};

/** Suite CRM account health bar fill (discrete demo widths). */
export const SAAS_CRM_HEALTH_BAR_WIDTH_GOOD: CSSProperties = { width: "92%" };
export const SAAS_CRM_HEALTH_BAR_WIDTH_WARN: CSSProperties = { width: "48%" };

// --- Suite / demo placeholders ---
export const SUITE_CARD_MIN_HEIGHT_280: CSSProperties = { minHeight: 280 };
export const SUITE_PLACEHOLDER_MIN_200: CSSProperties = { minHeight: 200 };
export const SUITE_DARK_CARD_PREVIEW_120: CSSProperties = {
  height: 120,
  background: "rgba(255,255,255,0.08)"
};

export const SUITE_MAP_PLACEHOLDER_MIN_220: CSSProperties = { minHeight: 220 };
export const SUITE_LOGISTICS_SHIPMENT_MAP: CSSProperties = { minHeight: 140 };

export const SUITE_DEMO_CHART_SHELL_200: CSSProperties = { height: 200 };
export const SUITE_INDUSTRIAL_TELEMETRY_PLACEHOLDER: CSSProperties = {
  height: 200
};

export const SUITE_ADVERTS_BAR_SHELL_160: CSSProperties = { height: 160 };
export const SUITE_ADVERTS_TREND_PLACEHOLDER_220: CSSProperties = {
  height: 220
};

export const SUITE_PROGRESS_HEIGHT_6: CSSProperties = { height: 6 };
export const SUITE_PROGRESS_HEIGHT_8: CSSProperties = { height: 8 };
export const SUITE_PROGRESS_HEIGHT_10: CSSProperties = { height: 10 };

/** HR suite requisition funnel — static demo segment widths. */
export const HR_REQUISITION_FUNNEL_SEGMENT_STYLES: readonly CSSProperties[] = (
  [
    [100, 1],
    [78, 0.85],
    [45, 0.7],
    [22, 0.55]
  ] as const
).map(([w, opacity]) => ({
  ...SUITE_PROGRESS_HEIGHT_8,
  width: `${w}px`,
  opacity
}));

export const SUITE_DEVOPS_BUDGET_ROW: CSSProperties = { height: 28 };
export const SUITE_INDUSTRIAL_OEE_ROW: CSSProperties = { height: 48 };

export const SUITE_ECOM_PRODUCT_THUMB: CSSProperties = {
  height: 160,
  objectFit: "cover"
};

export const SUITE_ECOM_RETURN_TIMELINE_DOT: CSSProperties = {
  width: 12,
  height: 12,
  left: -22,
  top: 6
};

export const SUITE_SEC_POLICY_RING: CSSProperties = {
  width: 100,
  height: 100
};

export const INTERACTIVE_CHIP: CSSProperties = { cursor: "pointer" };

export const SUITE_FINANCE_TIMELINE_DOT: CSSProperties = {
  width: 10,
  height: 10,
  left: -18,
  top: 6
};

export const SUITE_CRM_CONTACT_AVATAR: CSSProperties = {
  width: 44,
  height: 44,
  minWidth: 44
};

export const SUITE_HR_EMPLOYEE_TILE_AVATAR: CSSProperties = {
  width: 64,
  height: 64
};

export const DASHBOARD_HR_ORG_INITIALS: CSSProperties = {
  width: 48,
  height: 48,
  fontSize: 14
};

export const FUNNEL_COUNT_LABEL_CENTER: CSSProperties = {
  left: "50%",
  top: "50%",
  transform: "translate(-50%,-50%)"
};

export const LOGISTICS_MAP_FRAME: CSSProperties = {
  height: 320,
  background:
    "linear-gradient(145deg, rgba(99,102,241,0.12) 0%, rgba(14,165,233,0.08) 45%, rgba(34,197,94,0.06) 100%)",
  backgroundImage:
    "repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(0,0,0,0.04) 31px, rgba(0,0,0,0.04) 32px), repeating-linear-gradient(90deg, transparent, transparent 31px, rgba(0,0,0,0.04) 31px, rgba(0,0,0,0.04) 32px)"
};

export const LOGISTICS_MAP_PIN_ANCHOR: CSSProperties = {
  transform: "translate(-50%, -50%)"
};

export const LOGISTICS_MAP_LOAD_LABEL: CSSProperties = { minWidth: 72 };

/** Main logistics dashboard map — preset pin positions (index-aligned). */
export const LOGISTICS_MAP_STOP_PIN_STYLES: readonly CSSProperties[] = [
  { ...LOGISTICS_MAP_PIN_ANCHOR, top: "18%", left: "42%" },
  { ...LOGISTICS_MAP_PIN_ANCHOR, top: "52%", left: "38%" },
  { ...LOGISTICS_MAP_PIN_ANCHOR, top: "48%", left: "62%" },
  { ...LOGISTICS_MAP_PIN_ANCHOR, top: "62%", left: "18%" },
  { ...LOGISTICS_MAP_PIN_ANCHOR, top: "28%", left: "72%" }
];

export const LOGISTICS_MAP_STOP_PIN_FALLBACK: CSSProperties = {
  ...LOGISTICS_MAP_PIN_ANCHOR,
  top: "40%",
  left: "50%"
};

// --- Shortcodes / misc ---
export const SHORTCODE_LINK_DEMO: CSSProperties = {
  textDecoration: "underline",
  color: "blue"
};

export const GALLERY_THUMB_OBJECT_COVER: CSSProperties = {
  objectFit: "cover",
  width: "100%",
  height: "100%"
};

// --- Education learner activity heat (static on/off) ---
export const EDU_ACTIVITY_ON: CSSProperties = {
  width: 10,
  height: 24,
  background: "#28a745"
};

export const EDU_ACTIVITY_OFF: CSSProperties = {
  width: 10,
  height: 24,
  background: "#e9ecef"
};
